/* LOCAL STORAGE BACKEND — IndexedDB
 *
 * PHASE 1, STEP 3. This file is NEW CODE, not moved code: it is the first
 * file in src/ that scripts/extract.mjs does not generate. Everything else
 * in src/ is the original single-file app, split up; this is the piece that
 * was never written before, because in the Claude artifact sandbox
 * `window.storage` was provided by the host.
 *
 * It implements exactly the four methods the app already calls, with the
 * same shapes, so none of the 12 call sites change:
 *
 *   get(key, shared)     -> { key, value } | null      (null when absent)
 *   set(key, value, shared)  -> { key }                 (rejects on failure)
 *   delete(key, shared)  -> { key }
 *   list(prefix, shared) -> { keys: [...] }             (full keys, not suffixes)
 *
 * `shared` is the app's public/community namespace: `false` is this
 * collector's private data, `true` is what a published profile exposes. Two
 * object stores keep them apart, which is the local stand-in for the row-
 * level split that Supabase RLS will enforce in Phase 3.
 *
 * WHY INDEXEDDB AND NOT localStorage: note photos are stored as base64 data
 * URLs. A single phone photo is comfortably over a megabyte once base64'd,
 * so a vault of any size blows past localStorage's ~5 MB ceiling almost
 * immediately — and localStorage fails by throwing on write, i.e. the user
 * loses a photo they think they saved. IndexedDB is quota-managed in the
 * hundreds of MB and stores strings without a size cliff.
 *
 * PHASE 3: this file is what gets replaced (by Supabase + R2) behind the
 * unchanged seam in storage.js. Nothing above it should need to know.
 */

const DB_NAME = 'currency-keep'
const DB_VERSION = 1
const PRIVATE = 'private'
const SHARED = 'shared'

// Resolve the IndexedDB globals through globalThis first so this module also
// works under the smoke test, where the DOM is jsdom (which has no IndexedDB)
// and the IndexedDB implementation is a separate global. In a browser
// globalThis IS window, so both lookups find the same object.
const idbFactory = () =>
  globalThis.indexedDB ?? (typeof window !== 'undefined' ? window.indexedDB : undefined)
const keyRange = () =>
  globalThis.IDBKeyRange ?? (typeof window !== 'undefined' ? window.IDBKeyRange : undefined)

const storeFor = (shared) => (shared ? SHARED : PRIVATE)

/* ── ⚠⚠ WHAT A VALUE IS ALLOWED TO BE — PHASE 4, STEP 6 ─────────────
 * Every value in this store was a STRING from Phase 1 until 2026-08-21,
 * and `set` coerced with `String(value)` to guarantee it. Step 6 stores
 * the ORIGINAL photo bytes so the encoder can do the rotate/crop at full
 * resolution, and those are a `File`/`Blob` — the one thing that cannot
 * survive being turned into a string.
 *
 * ⚠ AND THE OLD COERCION FAILED SILENTLY, WHICH IS WHY THIS IS A
 * FUNCTION AND NOT AN INLINE TERNARY. `String(blob)` is
 * `"[object Blob]"` — 15 characters, no error, no warning, and a photo
 * that is gone. The app would keep working off its preview and nothing
 * would surface until Step 7 uploaded 15 bytes. That is trap 2 exactly:
 * a plausible-looking result that is nothing like the input.
 *
 * ⚠ STRINGS BEHAVE EXACTLY AS BEFORE. The coercion is not relaxed — it
 * is narrowed by one case. Anything that is not binary still goes through
 * `String()`, so numbers, booleans and accidental objects coerce the way
 * every caller since Phase 1 has depended on. `Blob` is feature-detected
 * because this module also runs under jsdom in `smoke`.
 *
 * ⚠ THE SPEC IS NOT BINARY, DELIBERATELY. `{angle, crop}` is written as
 * JSON, so exactly one kind of value in this store is not a string and it
 * is the one that has no choice. */
const storable = (value) =>
  (typeof Blob !== 'undefined' && value instanceof Blob) ||
  value instanceof ArrayBuffer ||
  ArrayBuffer.isView(value)
    ? value
    : String(value)

let _open = null

/* One connection, opened lazily and reused. The promise is cached rather
   than the database, so concurrent first calls share a single open request
   instead of racing two. A failed open clears the cache so a later call can
   try again rather than being permanently poisoned. */
function db() {
  if (_open) return _open
  _open = new Promise((resolve, reject) => {
    const factory = idbFactory()
    if (!factory) { reject(new Error('IndexedDB is not available')); return }
    const req = factory.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const d = req.result
      for (const name of [PRIVATE, SHARED]) {
        if (!d.objectStoreNames.contains(name)) d.createObjectStore(name, { keyPath: 'key' })
      }
    }
    req.onsuccess = () => {
      const d = req.result
      // Another tab upgrading the schema would otherwise be blocked forever
      // by this connection. Drop it and let the next call re-open.
      d.onversionchange = () => { d.close(); _open = null }
      resolve(d)
    }
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'))
  })
  _open.catch(() => { _open = null })
  return _open
}

/* Run one request in its own transaction.
 *
 * Resolution waits for `oncomplete`, NOT for the request's `onsuccess`. On a
 * write those are different moments: a put can succeed and the transaction
 * can still abort afterwards (quota exceeded is the case that matters here,
 * with photos). Resolving on request success would report a save that never
 * landed — the app would toast "saved" over data that is gone on reload. */
function run(shared, mode, fn) {
  return db().then((d) => new Promise((resolve, reject) => {
    let t
    try { t = d.transaction(storeFor(shared), mode) }
    catch (e) { reject(e); return }
    let out
    const req = fn(t.objectStore(storeFor(shared)))
    if (req) req.onsuccess = () => { out = req.result }
    t.oncomplete = () => resolve(out)
    t.onerror = () => reject(t.error || (req && req.error) || new Error('IndexedDB transaction failed'))
    t.onabort = () => reject(t.error || (req && req.error) || new Error('IndexedDB transaction aborted'))
  }))
}

const backend = {
  async get(key, shared) {
    const rec = await run(shared, 'readonly', (s) => s.get(String(key)))
    // Absent keys MUST come back as null, not as {value: undefined}: state.js
    // does `r ? JSON.parse(r.value) : []`, so a truthy record with no value
    // would throw on a first run instead of starting an empty vault.
    return rec ? { key: rec.key, value: rec.value } : null
  },

  async set(key, value, shared) {
    const k = String(key)
    await run(shared, 'readwrite', (s) => s.put({ key: k, value: storable(value) }))
    return { key: k }
  },

  async delete(key, shared) {
    const k = String(key)
    await run(shared, 'readwrite', (s) => s.delete(k))
    return { key: k }
  },

  async list(prefix, shared) {
    const p = prefix == null ? '' : String(prefix)
    // ￿ is the highest UTF-16 code unit, so [p, p+￿] is exactly the
    // set of keys beginning with p under IndexedDB's code-unit ordering.
    const range = p ? keyRange().bound(p, p + '￿') : undefined
    const keys = await run(shared, 'readonly', (s) => s.getAllKeys(range))
    // Callers feed these straight back into get() and delete(), so they are
    // whole keys — the prefix is not stripped.
    return { keys: keys || [] }
  },
}

/* Ask the browser not to evict the vault under storage pressure. Best effort
   and deliberately not awaited: Chrome grants it automatically once the PWA
   is installed, Safari and Firefox may prompt or decline, and none of that
   should delay the first paint. A refusal costs eviction risk, not
   correctness. */
function requestPersistence() {
  try {
    const s = typeof navigator !== 'undefined' && navigator.storage
    if (!s || !s.persist) return
    Promise.resolve(s.persisted ? s.persisted() : false)
      .then((already) => (already ? true : s.persist()))
      .catch(() => {})
  } catch (e) { /* non-fatal by design */ }
}

/* Attach the backend as window.storage.
 *
 * MUST run before storage.js evaluates `const HAS = ... && window.storage`,
 * which is why storage.js calls this at the top of its own body rather than
 * anything calling it from outside. See scripts/extract.mjs.
 *
 * If IndexedDB is missing or blocked (private browsing on some engines), we
 * install NOTHING. HAS then stays false and the app behaves exactly as it
 * did before this step: it browses the catalog fine and does not persist.
 * That is a much better failure than installing a backend that throws on
 * every call — it is the difference between "does not save" and "breaks".
 */
function installLocalStorage() {
  if (typeof window === 'undefined') return null
  // Never clobber a host-provided backend (the artifact sandbox had one).
  if (window.storage) return window.storage
  if (!idbFactory()) {
    console.warn('Currency Keep: IndexedDB unavailable — the app will run but will not save.')
    return null
  }
  window.storage = backend
  requestPersistence()
  return backend
}

/* `requestPersistence` is exported for src/remote.js (Phase 3 Step 4).
 * Once Supabase is behind the seam this file is the PHOTO backend, and
 * remote.js does the installing — but the photos are still local, so the
 * eviction request still matters and still belongs to whoever owns the
 * IndexedDB store. */
export { backend, installLocalStorage, requestPersistence, DB_NAME, DB_VERSION, PRIVATE, SHARED }
